'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { requestFactory } from './utils'
import { UseApiOptions } from './model'
import { useToast } from '@components/shared/toaster/toaster.ts'
import { useClearSession } from '@domain/auth/useClearSession.hook'
import { useAuthTokenManager } from '@domain/auth/AuthTokenManager.context'
import { useAuthContext } from '@domain/auth/Auth.context.tsx'
import { useRouter } from 'next/navigation'
import { paths } from '@router'
import { formDataFromJSON } from './http/utils'
import { queryClient } from '@providers/react-query'
import { standaloneApi } from './standaloneApi'
import type { ApiGETFn } from './standaloneApi'

// Use function declaration to ensure it's hoisted and available immediately
export function useApi(_: UseApiOptions = {}) {
  // SSR check - we still need to call all hooks to follow Rules of Hooks
  const isSSR = typeof window === 'undefined'

  const { clearSession } = useClearSession()
  const { requestToken } = useAuthTokenManager()
  const { toast } = useToast()
  const { setIsBannedUserModalOpen } = useAuthContext()
  const router = useRouter()

  const handleUnauthorized = useCallback(
    async ({ shouldRedirectToRoot = true, shouldShowErrorToast = true }) => {
      if (shouldShowErrorToast) {
        toast({
          title: 'Session timed out. Please sign in again to continue',
          variant: 'error',
        })
      }

      await clearSession()

      queryClient.clear()

      if (shouldRedirectToRoot) {
        router.push(paths.root)
      }
    },
    [clearSession, toast],
  )

  const handleBanned = useCallback(async () => {
    setIsBannedUserModalOpen(true)
    await clearSession()
    queryClient.clear()

    return
  }, [clearSession, toast])

  const get = useCallback(
    requestFactory({
      method: 'GET',
      requestToken,
      handleUnauthorized,
      handleBanned,
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )
  const post = useCallback(
    requestFactory({
      method: 'POST',
      requestToken,
      handleUnauthorized,
      handleBanned,
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )
  const patch = useCallback(
    requestFactory({
      method: 'PATCH',
      requestToken,
      handleUnauthorized,
      handleBanned,
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )
  const put = useCallback(
    requestFactory({
      method: 'PUT',
      requestToken,
      handleUnauthorized,
      handleBanned,
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )
  const deleteReq = useCallback(
    requestFactory({
      method: 'DELETE',
      requestToken,
      handleUnauthorized,
      handleBanned,
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )

  const uploadFile = useCallback(
    requestFactory({
      method: 'POST',
      requestToken,
      handleUnauthorized,
      handleBanned,
      initialRequestConfig: {
        sendRawBody: true,
        bodyTransformer: (body) => formDataFromJSON(body),
        autoContentType: true,
      },
    }) as ApiGETFn,
    [requestToken, handleUnauthorized, handleBanned],
  )

  useEffect(() => {
    if (!isSSR) {
      standaloneApi.get = get
      standaloneApi.post = post
      standaloneApi.patch = patch
      standaloneApi.put = put
    }
  }, [get, post, patch, put, isSSR])

  const apiMethods = useMemo(
    () => ({
      get,
      post,
      patch,
      put,
      deleteReq,
      uploadFile,
    }),
    [get, post, patch, put, deleteReq, uploadFile],
  )

  if (isSSR) {
    const noOpFn: ApiGETFn = () => () =>
      Promise.reject(new Error('API calls are not available during SSR'))
    return {
      get: noOpFn,
      post: noOpFn,
      patch: noOpFn,
      put: noOpFn,
      deleteReq: noOpFn,
      uploadFile: noOpFn,
    }
  }

  return apiMethods
}
